/**
 * @file ProductEditPage.tsx
 * @description Карточка товара (phase08.5; phase19.31 — разбор B, SC50, SC67):
 * форма с боковой колонкой (основная до 960 + боковая 360).
 *
 * Шапка — в верхней панели (`CabinetPageHeader`): «назад» к реестру,
 * название, статус пилюлей, под ним «Сохранено в 14:32» / «Есть несохранённые
 * изменения»; видимые кнопки «Опубликовать / Снять с публикации», «В архив»
 * (последней, с подтверждением) и главное «Сохранить» (на телефоне — в
 * нижней полосе, остальные строкой под шапкой). Название важнее кнопок
 * (QA-5 D13): «Отменить изменения» и «Опубликовать» — в панели только от
 * 1280, «В архив» — всегда строкой под шапкой (`ProductHeaderActions`).
 *
 * Основная колонка — поля товара по блокам; боковая — фото, фасовки
 * (компактным списком), остатки по складам. Вкладок у карточки нет: блоки
 * идут подряд. Будущая вкладка «Тех.карта» (phase19.21) появится в окне
 * фасовки, а не здесь (SC50). Состояние и сохранение — `useProductCard`.
 */

import { Save } from "lucide-react";
import { useParams, useSearchParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { StatusPill } from "../../../components/ui/StatusPill";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { PRODUCT_STATUS_META } from "../../../lib/catalog-status";
import { ProductBasicSection } from "./sections/ProductBasicSection";
import { ProductCertificatesSection } from "./sections/ProductCertificatesSection";
import { ProductChannelsSection } from "./sections/ProductChannelsSection";
import { ProductCostSection } from "./sections/ProductCostSection";
import { ProductDetailsSection } from "./sections/ProductDetailsSection";
import { ProductGallerySection } from "./sections/ProductGallerySection";
import { ProductHeaderActions } from "./sections/ProductHeaderActions";
import { ProductInventorySection } from "./sections/ProductInventorySection";
import { ProductRegulatorySection } from "./sections/ProductRegulatorySection";
import { ProductSeoSection } from "./sections/ProductSeoSection";
import { ProductTaxonomySection } from "./sections/ProductTaxonomySection";
import { GallerySectionPlaceholder, InventorySectionPlaceholder } from "./sections/Placeholders";
import { StockBreakdownSection } from "./sections/StockBreakdownSection";
import { useProductCard } from "./sections/useProductCard";

/** Подпись под названием: что с изменениями. */
function saveState(dirty: boolean, savedAt: Date | null): string | undefined {
  if (dirty) return "Есть несохранённые изменения";
  if (!savedAt) return undefined;
  return `Сохранено в ${savedAt.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`;
}

export function ProductEditPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const [searchParams] = useSearchParams();
  const card = useProductCard(slug, id, searchParams.get("category") ?? "");
  const canViewFinance = useHasPermission("SHOP_VIEW_FINANCE");
  const { product, form, saving, isNew } = card;
  const back = { to: `/shop/${slug}/catalog/products`, label: "К товарам" };
  const category = form.categoryId ? (card.categories.find((c) => c.id === form.categoryId) ?? null) : null;

  if (card.loading || (!isNew && !product)) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Товар" back={back} />
        {card.loading ? <ListSkeleton rows={6} compact /> : <ListError message={card.error ?? "Товар не найден"} />}
      </div>
    );
  }

  const canSave = !saving && (isNew ? !!form.categoryId && !!form.displayName.trim() : card.dirty);
  // Кнопки шапки по частям: название важнее кнопок (QA-5 D13).
  const headerActions = product
    ? {
        product,
        dirty: card.dirty,
        saving,
        onDiscard: card.discard,
        onTogglePublish: () => void card.togglePublish(),
        onArchive: () => void card.archive(),
      }
    : null;
  const archived = product?.status === "ARCHIVED";

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={isNew ? "Новый товар" : (product?.displayName ?? "Товар")}
        subtitle={saveState(card.dirty, card.savedAt)}
        status={product ? <StatusPill meta={PRODUCT_STATUS_META[product.status]} /> : undefined}
        back={back}
        panelActions={headerActions && (card.dirty || !archived) ? <ProductHeaderActions part="panel" {...headerActions} /> : undefined}
        secondaryActions={headerActions && !archived ? <ProductHeaderActions part="row" {...headerActions} /> : undefined}
        secondaryBelow
        action={
          <Button variant="brand" onClick={() => void card.save()} disabled={!canSave} loading={saving}>
            <Save className="size-4" />
            {isNew ? "Создать товар" : "Сохранить"}
          </Button>
        }
      />

      {card.error ? <NoticeStrip tone="error">{card.error}</NoticeStrip> : null}

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,960px)_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          <ProductBasicSection form={form} categories={card.categories} onChange={card.patch} disabled={saving} savedSlug={product?.slug ?? null} />
          <ProductTaxonomySection form={form} categories={card.categories} kinds={card.kinds} isNew={isNew} onChange={card.patch} disabled={saving} />
          <ProductDetailsSection form={form} onChange={card.patch} disabled={saving} />
          <ProductSeoSection form={form} product={product} onChange={card.patch} disabled={saving} />
          <ProductRegulatorySection form={form} category={category} onChange={card.patch} disabled={saving} />
          {product ? <ProductCertificatesSection producerId={form.producerId || null} /> : null}
          {product ? <ProductChannelsSection productId={product.id} /> : null}
          {product && canViewFinance ? <ProductCostSection productId={product.id} /> : null}
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {product ? <ProductGallerySection productId={product.id} /> : <GallerySectionPlaceholder />}
          {product ? (
            <ProductInventorySection
              productId={product.id}
              productSlug={product.slug}
              productName={product.displayName}
              kindLabelTemplate={product.effectiveKind?.labelTemplate ?? null}
              category={category}
            />
          ) : (
            <InventorySectionPlaceholder />
          )}
          {/* Остатки по складам и партиям (phase19.14, W9) — только у сохранённого товара. */}
          {product ? <StockBreakdownSection productId={product.id} /> : null}
        </div>
      </div>

      <ConfirmDialog request={card.confirmRequest} onSettle={card.settleConfirm} />
    </div>
  );
}
